import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { IndexDef, LevelCode } from '@/api/types/readiness'
import { LEVELS } from '@/api/types/readiness'
import { levelName } from '@/lib/indexes/registry'
import { RdModal, RdField } from '@/components/readiness/RdModal'
import { Icon } from '@/components/readiness/primitives'

const today = () => new Date().toISOString().slice(0, 10)
const defaultName = () => new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

// ── Save snapshot (§9.1) ────────────────────────────────────────────────────
export function SaveSnapshotModal({ unconfirmed, onClose, onSave }: { unconfirmed: number; onClose: () => void; onSave: (name: string, note: string) => void }) {
  const [name, setName] = useState(defaultName())
  const [note, setNote] = useState('')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSave(name.trim() || defaultName(), note.trim())
  }
  return (
    <RdModal
      title="Save snapshot"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rd-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="rd-save-snapshot" className="rd-btn rd-btn-primary">Save snapshot</button>
        </>
      }
    >
      <form id="rd-save-snapshot" onSubmit={submit}>
        <RdField label="Name" htmlFor="snap-name">
          <input id="snap-name" className="rd-input" value={name} onChange={(e) => setName(e.target.value)} />
        </RdField>
        <RdField label="Note (optional)" htmlFor="snap-note" hint='e.g. "Pre-kickoff baseline"'>
          <input id="snap-note" className="rd-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this snapshot matters" />
        </RdField>
        {unconfirmed > 0 && (
          <p className="mt-2 flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' }}>
            <Icon name="warn" size={14} className="mt-0.5 shrink-0" />
            <span>
              {unconfirmed} suggested {unconfirmed === 1 ? 'level is' : 'levels are'} not confirmed and will be recorded as {unconfirmed === 1 ? 'its' : 'their'} last confirmed level (or L0).{' '}
              <Link to="/readiness?filter=needs_confirmation" className="rd-link underline" onClick={onClose}>Confirm them first →</Link>
            </span>
          </p>
        )}
        <p className="mt-3 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>
          A snapshot stores every objective's level, status, provenance and evidence, the score, the index version, and who saved it. Snapshots are immutable.
        </p>
      </form>
    </RdModal>
  )
}

// ── Add evidence (§8.6) ─────────────────────────────────────────────────────
export interface EvidenceInput {
  kind: 'file' | 'note'
  title: string
  summary: string
  link: string | null
  file_name: string | null
  occurred_at: string
  suggest_level: LevelCode | null
}

export function EvidenceModal({ index, objectiveCode, manualOnly, onClose, onAdd }: { index: IndexDef; objectiveCode: string; manualOnly: boolean; onClose: () => void; onAdd: (input: EvidenceInput) => void }) {
  const [kind, setKind] = useState<'file' | 'note'>('note')
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [url, setUrl] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [date, setDate] = useState(today())
  const [suggest, setSuggest] = useState(false)
  const [level, setLevel] = useState<LevelCode>('L1')
  const [dragging, setDragging] = useState(false)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    onAdd({
      kind,
      title: title.trim(),
      summary: summary.trim(),
      link: kind === 'note' ? url.trim() || null : null,
      file_name: kind === 'file' ? fileName : null,
      occurred_at: new Date(date).toISOString(),
      suggest_level: suggest ? level : null,
    })
  }
  return (
    <RdModal
      title={`Add evidence · ${objectiveCode}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rd-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="rd-add-evidence" className="rd-btn rd-btn-primary" disabled={!title.trim()}>Add evidence</button>
        </>
      }
    >
      <form id="rd-add-evidence" onSubmit={submit}>
        <fieldset className="mb-3">
          <legend className="mb-1 block text-[12.5px] font-medium" style={{ color: 'var(--rd-text-secondary)' }}>Type</legend>
          <div className="flex gap-2">
            {(['file', 'note'] as const).map((k) => (
              <label key={k} className="rd-btn cursor-pointer" style={kind === k ? { borderColor: 'var(--rd-accent-primary)', color: 'var(--rd-accent-link)' } : undefined}>
                <input type="radio" name="ev-kind" value={k} checked={kind === k} onChange={() => setKind(k)} className="sr-only" />
                {k === 'file' ? 'File' : 'Note with link'}
              </label>
            ))}
          </div>
        </fieldset>
        <RdField label="Title" htmlFor="ev-title" required>
          <input id="ev-title" className="rd-input" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g. Vendor assessment · Thales Luna HSM" />
        </RdField>
        <RdField label="Summary" htmlFor="ev-summary">
          <textarea id="ev-summary" className="rd-input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What this shows and why it supports the level" />
        </RdField>
        {kind === 'file' ? (
          <RdField label="File" htmlFor="ev-file" hint="Files are kept with the evidence item. Iteration 1 stores the file name and the note; storage lands with the backend.">
            <label
              htmlFor="ev-file"
              onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) setFileName(f.name) }}
              className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-[10px] px-4 py-6 text-center text-[12.5px]"
              style={{ border: `1px dashed ${dragging ? 'var(--rd-accent-primary)' : 'var(--rd-border-control)'}`, color: 'var(--rd-text-muted)', background: 'var(--rd-bg-tile)' }}
            >
              <Icon name="file" size={18} />
              {fileName ? <span style={{ color: 'var(--rd-text-primary)' }}>{fileName}</span> : <span>Drop a file here or click to choose</span>}
              <input id="ev-file" type="file" className="sr-only" onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)} />
            </label>
          </RdField>
        ) : (
          <RdField label="URL (optional)" htmlFor="ev-url">
            <input id="ev-url" className="rd-input" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" />
          </RdField>
        )}
        <RdField label="Occurred on" htmlFor="ev-date">
          <input id="ev-date" className="rd-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </RdField>
        {manualOnly && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[14px]">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={suggest} onChange={(e) => setSuggest(e.target.checked)} className="rounded" />
              Also suggest this level:
            </label>
            <select aria-label="Suggested level" className="rd-input !w-auto" value={level} disabled={!suggest} onChange={(e) => setLevel(e.target.value as LevelCode)}>
              {LEVELS.map((l) => (
                <option key={l} value={l}>{l} · {levelName(index, l)}</option>
              ))}
            </select>
          </div>
        )}
      </form>
    </RdModal>
  )
}

// ── Assign owner / set due date / link a recommendation (§8.1, §8.4) ────────
export function AssignModal({ title, initialOwner, initialDue, confirmLabel = 'Save', onClose, onSave }: { title: string; initialOwner?: string | null; initialDue?: string | null; confirmLabel?: string; onClose: () => void; onSave: (owner: string | null, due: string | null) => void }) {
  const [owner, setOwner] = useState(initialOwner ?? '')
  const [due, setDue] = useState(initialDue ? initialDue.slice(0, 10) : '')
  return (
    <RdModal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rd-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="rd-btn rd-btn-primary" onClick={() => onSave(owner.trim() || null, due ? new Date(due).toISOString() : null)}>{confirmLabel}</button>
        </>
      }
    >
      <RdField label="Owner" htmlFor="as-owner" hint="Name or email of the person accountable.">
        <input id="as-owner" className="rd-input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="e.g. Priya Nair" />
      </RdField>
      <RdField label="Due date" htmlFor="as-due">
        <input id="as-due" className="rd-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
      </RdField>
    </RdModal>
  )
}

export function TargetLevelModal({ initial, onClose, onSave }: { initial: string | null; onClose: () => void; onSave: (v: string | null) => void }) {
  const [v, setV] = useState(initial ?? '')
  return (
    <RdModal
      title="Target level"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rd-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="rd-btn rd-btn-primary" onClick={() => onSave(v.trim() || null)}>Save</button>
        </>
      }
    >
      <RdField label="Target" htmlFor="tl-v" hint="Free text, e.g. L2 by Dec 2026">
        <input id="tl-v" className="rd-input" value={v} onChange={(e) => setV(e.target.value)} placeholder="L2 by Dec 2026" />
      </RdField>
    </RdModal>
  )
}
